import type { Shell } from '@kollez/shared';

export interface NavItem {
  key: string;
  label: string;
  href: string;
  icon?: string;                  // lucide icon name
  count?: number;
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * Per-shell nav config. Route paths align with the router in `router.tsx`.
 *
 * Page 61/N — sidebar completeness audit (autonomous 8-hour pass).
 * Every routed page a role has permission to open now has a sidebar entry
 * under a semantic group. Cross-checked against router.tsx path list:
 *   student:   home / explore / applications / offers / interviews /
 *              programs / internships / saved / profile-360 / resume-builder /
 *              mock / prep / advisor / tests / skills / projects / documents /
 *              upload-marks / academics / stats / profile / memberships /
 *              nexie / notifications / help / settings / privacy
 *   tpo:       today / drives / calendar / offers / students / claim /
 *              partners / mous / graduation / reports / nexie / notifications /
 *              help / settings
 *   group:     overview / structure / colleges / drives / students / team /
 *              partners / mous / compliance / analytics / reports / billing /
 *              settings / nexie / notifications / help
 *   company:   campus-hiring / openings / candidates / offers / assessments /
 *              colleges / team / earnings / nexie / notifications / help /
 *              settings
 *   platform:  dashboard / approvals / fraud / tenants / users / plans /
 *              invoices / health / audit / AISHE / flags / nexie audit /
 *              help
 *   principal: overview (+ nexie / notifications / help)
 *   hod:       department (+ nexie / notifications / help)
 *   faculty:   mentees (+ nexie / notifications / help)
 *
 * Trailing "Chat + Inbox + Help" group is applied identically to every
 * non-platform shell so the shared Nexie/notifications/help/settings
 * pages are one click away no matter who's logged in.
 */

const SHARED_TAIL: NavGroup = {
  label: 'Assist',
  items: [
    { key: 'nexie', label: 'Ask Nexie', href: '/nexie', icon: 'sparkles' },
    { key: 'notifications', label: 'Notifications', href: '/notifications', icon: 'bell' },
    { key: 'help', label: 'Help', href: '/help', icon: 'help-circle' },
  ],
};

export const NAV: Record<Shell, NavGroup[]> = {
  student: [
    { items: [{ key: 'home', label: 'Home', href: '/home', icon: 'house' }] },
    {
      label: 'Jobs',
      items: [
        { key: 'opportunities', label: 'Explore', href: '/explore', icon: 'briefcase' },
        { key: 'applications', label: 'My applications', href: '/applications', icon: 'list-checks' },
        { key: 'interviews', label: 'Interviews', href: '/interviews', icon: 'calendar' },
        { key: 'offers', label: 'Offers', href: '/offers', icon: 'file-signature' },
        { key: 'saved', label: 'Saved', href: '/saved', icon: 'star' },
      ],
    },
    {
      label: 'Grow',
      items: [
        { key: 'assignments', label: 'Assignments', href: '/assignments', icon: 'inbox' },
        { key: 'programs', label: 'Programmes', href: '/programs', icon: 'rocket' },
        { key: 'internships', label: 'Internships', href: '/internships', icon: 'briefcase' },
        { key: 'mock', label: 'Mock interview', href: '/mock', icon: 'mic' },
        { key: 'prep', label: 'Prep plan', href: '/prep', icon: 'calendar-check' },
        { key: 'tests', label: 'Tests', href: '/tests', icon: 'clipboard-check' },
        { key: 'advisor', label: 'Career advisor', href: '/advisor', icon: 'compass' },
      ],
    },
    {
      label: 'My profile',
      items: [
        { key: 'profile', label: 'Profile', href: '/profile', icon: 'user' },
        { key: 'profile-360', label: 'Readiness 360', href: '/profile-360', icon: 'target' },
        { key: 'resume-builder', label: 'Resume builder', href: '/resume-builder', icon: 'file-text' },
        { key: 'skills', label: 'Skills', href: '/skills', icon: 'wrench' },
        { key: 'projects', label: 'Projects', href: '/projects', icon: 'folder-check' },
        { key: 'academics', label: 'Academics', href: '/academics', icon: 'graduation-cap' },
        { key: 'upload-marks', label: 'Upload marks', href: '/upload-marks', icon: 'file-plus' },
        { key: 'documents', label: 'Documents', href: '/documents', icon: 'file-check-2' },
        { key: 'stats', label: 'Batch stats', href: '/stats', icon: 'bar-chart-3' },
        { key: 'memberships', label: 'Memberships', href: '/profile/memberships', icon: 'id-card' },
        { key: 'transactions', label: 'Transactions', href: '/transactions', icon: 'receipt' },
        { key: 'progress', label: 'Progress', href: '/progress', icon: 'line-chart' },
        { key: 'privacy', label: 'Privacy (DPDP)', href: '/settings/privacy', icon: 'shield-check' },
      ],
    },
    SHARED_TAIL,
  ],

  tpo: [
    { items: [{ key: 'today', label: 'Today', href: '/tpo/today', icon: 'zap' }] },
    {
      label: 'Placement pipeline',
      // Opportunities is the top of the funnel — every partner posting
      // visible to this college, all 10 types (drive/internship/workshop/
      // hackathon/…). Same feed the student sees at /explore.
      items: [
        { key: 'opportunities', label: 'Opportunities', href: '/tpo/opportunities', icon: 'briefcase' },
        { key: 'drives', label: 'Drives', href: '/tpo/drives', icon: 'building-2' },
        { key: 'offers', label: 'Offers', href: '/tpo/offers', icon: 'file-signature' },
        { key: 'calendar', label: 'Calendar', href: '/tpo/calendar', icon: 'calendar' },
      ],
    },
    {
      label: 'Logistics',
      // Room + holiday + graduation-batch + campus-invitations cluster
      // together — all pre-drive scheduling concerns. CampusInvitations page
      // exists but wasn't linked anywhere until this rewrite.
      items: [
        { key: 'graduation', label: 'Graduation batch', href: '/tpo/graduation', icon: 'graduation-cap' },
        { key: 'rooms', label: 'Rooms', href: '/tpo/rooms', icon: 'building-2' },
        { key: 'holidays', label: 'Holidays', href: '/tpo/holidays', icon: 'calendar-check' },
        { key: 'campus-invites', label: 'Campus invitations', href: '/tpo/campus-invitations', icon: 'mail' },
      ],
    },
    {
      label: 'People',
      items: [
        { key: 'students', label: 'Students', href: '/tpo/students', icon: 'users' },
        { key: 'claim', label: 'Claim queue', href: '/tpo/students/claim', icon: 'user-check' },
        { key: 'faculty', label: 'Faculty', href: '/faculty/directory', icon: 'graduation-cap' },
        { key: 'partners', label: 'Partners', href: '/tpo/partners', icon: 'briefcase' },
      ],
    },
    {
      label: 'Agreements',
      // TPO can view + sign MoUs at the college tenant level. Reuses
      // /group/mous which is role-neutral copy and gated on `mou:view`
      // (TPO has it in role-defaults). Filters by req.tenantScope.tenant
      // which for a TPO is their college.
      items: [
        { key: 'mous', label: 'MoUs', href: '/group/mous', icon: 'file-check-2' },
      ],
    },
    {
      label: 'Assessments',
      // Real routes accessible with 'assessment:create'/'assessment:review_answer'
      // etc. — TPO has all of them per role-defaults.ts.
      items: [
        { key: 'exams', label: 'Exams', href: '/assess/exams', icon: 'clipboard-check' },
        { key: 'bank', label: 'Question bank', href: '/assess/bank', icon: 'library' },
        { key: 'integrity', label: 'Integrity flags', href: '/tpo/integrity', icon: 'shield-alert' },
      ],
    },
    {
      label: 'DPDP',
      items: [
        { key: 'grievances', label: 'Grievances', href: '/tpo/grievances', icon: 'flag' },
        { key: 'rectification', label: 'Rectification', href: '/tpo/rectification', icon: 'file-signature' },
      ],
    },
    {
      label: 'Workflows',
      items: [
        { key: 'workflows', label: 'Workflows', href: '/workflows', icon: 'network' },
      ],
    },
    {
      label: 'Insights',
      items: [
        { key: 'reports', label: 'Reports', href: '/tpo/reports', icon: 'file-spreadsheet' },
      ],
    },
    SHARED_TAIL,
  ],

  group: [
    { items: [{ key: 'overview', label: 'Overview', href: '/group/overview', icon: 'layout-dashboard' }] },
    {
      label: 'Institutions',
      // Structural + rostered. The Axis-4 regulator surfaces (affiliations,
      // approvals, governing entities, compliance) used to live here too but
      // drowned the group. They now sit under 'Compliance · Axis 4' below.
      items: [
        { key: 'structure', label: 'Structure', href: '/group/structure', icon: 'network' },
        { key: 'institutions', label: 'Institutions', href: '/group/colleges', icon: 'building-2' },
        { key: 'programs', label: 'Programmes', href: '/group/programs', icon: 'book-open' },
        { key: 'branches', label: 'Branches', href: '/group/specializations', icon: 'tags' },
        { key: 'students', label: 'Students', href: '/group/students', icon: 'users' },
        { key: 'team', label: 'Team', href: '/group/team', icon: 'user-cog' },
      ],
    },
    {
      label: 'Placement pipeline',
      // Opportunities is the top of the group funnel — every partner
      // posting across every college under this group, across all 10
      // canonical types (drive/internship/workshop/hackathon/…).
      items: [
        { key: 'opportunities', label: 'Opportunities', href: '/group/opportunities', icon: 'briefcase' },
        { key: 'drives', label: 'Drives', href: '/group/drives', icon: 'send' },
      ],
    },
    {
      label: 'Assessments',
      // Group HQ has assessment perms in role-defaults.ts; the pages already
      // exist. Surfacing them here so exam adoption is one click away
      // instead of a URL to memorise.
      items: [
        { key: 'exams', label: 'Exams', href: '/assess/exams', icon: 'clipboard-check' },
        { key: 'bank', label: 'Question bank', href: '/assess/bank', icon: 'library' },
      ],
    },
    {
      label: 'Partners',
      items: [
        { key: 'partners', label: 'Partners', href: '/group/partners', icon: 'handshake' },
        { key: 'mous', label: 'MoUs', href: '/group/mous', icon: 'file-check-2' },
      ],
    },
    {
      label: 'Compliance · Axis 4',
      // NIRF/NAAC/AICTE surfaces — a distinct concern from partner MoUs. The
      // "one row per regulator per subject" invariant makes this its own
      // page family.
      items: [
        { key: 'affiliations', label: 'Affiliations', href: '/group/affiliations', icon: 'file-signature' },
        { key: 'approvals', label: 'Approvals', href: '/group/approvals', icon: 'shield-check' },
        { key: 'governing', label: 'Governing entities', href: '/group/governing-entities', icon: 'briefcase' },
        { key: 'compliance', label: 'Compliance', href: '/group/compliance', icon: 'shield-check' },
      ],
    },
    {
      label: 'Insights',
      items: [
        { key: 'analytics', label: 'Analytics', href: '/group/analytics', icon: 'bar-chart-3' },
        { key: 'reports', label: 'Reports', href: '/group/reports', icon: 'file-spreadsheet' },
      ],
    },
    {
      label: 'Automation',
      items: [
        { key: 'workflows', label: 'Workflows', href: '/workflows', icon: 'network' },
      ],
    },
    {
      label: 'Admin',
      items: [
        { key: 'billing', label: 'Billing', href: '/group/billing', icon: 'wallet' },
        { key: 'settings', label: 'Settings', href: '/group/settings', icon: 'settings' },
      ],
    },
    {
      label: 'DPDP',
      items: [
        { key: 'grievances', label: 'Grievances', href: '/group/grievances', icon: 'flag' },
        { key: 'rectification', label: 'Rectification', href: '/group/rectification', icon: 'file-signature' },
      ],
    },
    SHARED_TAIL,
  ],

  company: [
    { items: [{ key: 'campus-hiring', label: 'Campus hiring', href: '/company/campus-hiring', icon: 'target' }] },
    {
      label: 'Hiring',
      items: [
        { key: 'openings', label: 'Openings', href: '/company/openings', icon: 'briefcase' },
        { key: 'candidates', label: 'Candidates', href: '/company/candidates', icon: 'users' },
        { key: 'offers', label: 'Offers', href: '/company/offers', icon: 'file-signature' },
        { key: 'panel', label: 'My panels', href: '/company/panel/interviews', icon: 'user-check' },
      ],
    },
    {
      label: 'Assessments',
      items: [
        { key: 'exams', label: 'Exams', href: '/assess/exams', icon: 'clipboard-check' },
        { key: 'bank', label: 'Question bank', href: '/assess/bank', icon: 'library' },
      ],
    },
    {
      label: 'Automation',
      items: [
        { key: 'workflows', label: 'Workflows', href: '/workflows', icon: 'network' },
      ],
    },
    {
      label: 'Partners',
      items: [
        { key: 'colleges', label: 'Colleges', href: '/company/colleges', icon: 'building-2' },
      ],
    },
    {
      label: 'Admin',
      items: [
        { key: 'team', label: 'Team', href: '/company/team', icon: 'users' },
        { key: 'earnings', label: 'Earnings', href: '/company/earnings', icon: 'coins' },
      ],
    },
    SHARED_TAIL,
  ],

  principal: [
    { items: [{ key: 'overview', label: 'Overview', href: '/principal/overview', icon: 'layout-dashboard' }] },
    {
      label: 'College ops',
      items: [
        { key: 'drives', label: 'Drives', href: '/tpo/drives', icon: 'building-2' },
        { key: 'students', label: 'Students', href: '/tpo/students', icon: 'users' },
        { key: 'offers', label: 'Offers', href: '/tpo/offers', icon: 'file-signature' },
        { key: 'calendar', label: 'Calendar', href: '/tpo/calendar', icon: 'calendar' },
        { key: 'reports', label: 'Reports', href: '/tpo/reports', icon: 'file-spreadsheet' },
      ],
    },
    { items: [{ key: 'faculty', label: 'Faculty', href: '/faculty/directory', icon: 'users' }] },
    {
      label: 'DPDP',
      items: [
        { key: 'grievances', label: 'Grievances', href: '/principal/grievances', icon: 'flag' },
        { key: 'rectification', label: 'Rectification', href: '/principal/rectification', icon: 'file-signature' },
      ],
    },
    SHARED_TAIL,
  ],

  hod: [
    { items: [{ key: 'department', label: 'Department', href: '/hod/department', icon: 'network' }] },
    { items: [{ key: 'faculty', label: 'Faculty', href: '/hod/faculty', icon: 'users' }] },
    {
      // HoD has assessment:create/edit/publish/assign per role-defaults.ts;
      // the pages already exist under /assess/*.
      label: 'Assessments',
      items: [
        { key: 'exams', label: 'Exams', href: '/assess/exams', icon: 'clipboard-check' },
        { key: 'bank', label: 'Question bank', href: '/assess/bank', icon: 'library' },
      ],
    },
    {
      label: 'DPDP',
      items: [
        { key: 'grievances', label: 'Grievances', href: '/hod/grievances', icon: 'flag' },
        { key: 'rectification', label: 'Rectification', href: '/hod/rectification', icon: 'file-signature' },
      ],
    },
    SHARED_TAIL,
  ],

  faculty: [
    { items: [{ key: 'mentees', label: 'Mentees', href: '/faculty/mentees', icon: 'users' }] },
    // Faculty's own profile — page exists at /faculty/profile but was orphaned.
    { items: [{ key: 'profile', label: 'My profile', href: '/faculty/profile', icon: 'user' }] },
    SHARED_TAIL,
  ],

  platform: [
    { items: [{ key: 'dashboard', label: 'Dashboard', href: '/platform', icon: 'layout-dashboard' }] },
    {
      label: 'Approvals',
      items: [
        { key: 'approvals', label: 'Approvals', href: '/platform/approvals', icon: 'inbox' },
        { key: 'fraud', label: 'Fraud queue', href: '/platform/fraud', icon: 'shield-alert' },
      ],
    },
    {
      label: 'Tenants',
      items: [
        { key: 'tenants', label: 'Tenants', href: '/platform/tenants', icon: 'building-2' },
        { key: 'users', label: 'Users', href: '/platform/users', icon: 'users' },
      ],
    },
    {
      label: 'Billing',
      items: [
        { key: 'plans', label: 'Plans', href: '/platform/plans', icon: 'tags' },
        { key: 'invoices', label: 'Invoices', href: '/platform/invoices', icon: 'wallet' },
        { key: 'catalog-txns', label: 'Catalog revenue', href: '/platform/catalog-transactions', icon: 'receipt' },
      ],
    },
    {
      label: 'Platform',
      items: [
        { key: 'health', label: 'Health', href: '/platform/health', icon: 'heart-pulse' },
        { key: 'incidents', label: 'Incidents', href: '/platform/incidents', icon: 'shield-alert' },
        { key: 'analytics-agreements', label: 'Analytics agreements', href: '/platform/analytics-agreements', icon: 'file-signature' },
        { key: 'audit', label: 'Audit log', href: '/platform/audit', icon: 'scroll-text' },
        { key: 'colleges', label: 'AISHE master', href: '/platform/colleges', icon: 'library' },
        // Feature flags removed from sidebar 2026-09-28 — tenant-detail
        // page already exposes per-tenant flag state on its "Features"
        // tab, and no ops workflow currently needs the fleet-wide view
        // as a first-class destination. The page itself is still
        // routable at /platform/flags for the rare admin who needs it.
        { key: 'nexie-audit', label: 'Nexie audit', href: '/nexie/audit', icon: 'sparkles' },
        { key: 'workflows', label: 'Workflow templates', href: '/workflows', icon: 'network' },
      ],
    },
    { items: [{ key: 'help', label: 'Help', href: '/help', icon: 'help-circle' }] },
  ],
};

/** Human-readable shell display name for the top-left workspace card. */
export const SHELL_LABEL: Record<Shell, string> = {
  student: 'My Kollez',
  tpo: 'Placement Cell',
  group: 'Group HQ',
  company: 'Recruiter Workspace',
  principal: 'Principal Desk',
  hod: 'Department',
  faculty: 'Faculty',
  platform: 'Platform',
};
